import { mdiCurrencyUsd, mdiFilterVariant } from '@mdi/js';
import Icon from '@mdi/react';
import { Box, Button, Divider, Grid, Typography, useMediaQuery } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useUserContext } from '../../contexts/User.context';
import { Carteira as CarteiraModel } from '../../models/Carteira.model';
import { SituacaoTour } from '../../models/Tour.model';
import { TipoUsuario } from '../../models/Usuario';
import { Routes } from '../../routes/index.routes';
import { CarteiraService } from '../../services';
import UsuarioService from '../../services/Usuario.service';
import { Colors } from '../../shared/colors';
import usePageTitle from '../../shared/hooks/usePageTitle';
import GuidedTour, { GuidedTourStep } from '../components/GuidedTour';
import Loading from '../components/Loading';
import { findVisibleTarget } from '../components/PjGuidedTour';
import DrawerAdicionarSaldo from '../features/adicionar-saldo/DrawerAdicionarSaldo';
import ListarTransacoes, {
  ListaTransacoesRef
} from '../features/listar-transacoes/ListarTransacoes';
import ListarCartoes from '../features/ListarCartoes';

const actionButtonSx = {
  color: Colors.secondary,
  fontSize: 15,
  fontWeight: 500,
  height: 36,
  minWidth: 0,
  px: 1.25,
  textTransform: 'none'
};

const Home = () => {
  usePageTitle('Embarque já');
  const navigate = useNavigate();
  const location = useLocation();
  const { loading, user, userClaim } = useUserContext();
  const listaTransacoesRef = useRef<ListaTransacoesRef>(null);
  const extratoRef = useRef<HTMLDivElement>(null);
  const recarregarSaldoRef = useRef<HTMLButtonElement>(null);
  const filtrarRef = useRef<HTMLButtonElement>(null);
  const cartoesRef = useRef<HTMLDivElement>(null);
  const abasRef = useRef<HTMLDivElement>(null);
  const [carteiras, setCarteiras] = useState<CarteiraModel[]>([]);
  const [carteirasCarregadas, setCarteirasCarregadas] = useState(false);
  const [drawerAdicaoValorOpen, setDrawerAdicaoValorOpen] = useState(false);
  const [tourOpen, setTourOpen] = useState(false);
  const [dadosContaEl, setDadosContaEl] = useState<HTMLElement | null>(null);

  const finalizarTour = (situacao: SituacaoTour.Concluido | SituacaoTour.Dispensado) => {
    setTourOpen(false);
    void UsuarioService.finalizarTour(situacao).catch(() => undefined);
  };

  const funcionario = carteiras.length > 1;
  // Abaixo de 768px o carrossel de ListarCartoes mostra um cartão por vez.
  const umCartaoPorVez = useMediaQuery('(max-width: 767.98px)');

  const tourSteps: GuidedTourStep[] = [
    {
      title: 'Recarregar saldo',
      description: 'Recarregue sua Carteira Pessoa Física por aqui sempre que precisar.',
      target: recarregarSaldoRef
    },
    {
      title: 'Filtrar o extrato',
      description:
        'Filtre o extrato por período para achar uma movimentação. Também dá para exportar em CSV.',
      target: filtrarRef
    },
    ...(funcionario
      ? [
          {
            title: 'Seus cartões',
            description: `Você tem dois cartões: o pessoal e o da empresa. O da empresa usa o saldo que ela transfere para você.${
              umCartaoPorVez ? ' Deslize para ver o outro.' : ''
            }`,
            target: cartoesRef
          },
          {
            title: 'Pessoais ou da empresa',
            description:
              'Escolha aqui se quer ver as movimentações do seu cartão pessoal ou do cartão da empresa.',
            target: abasRef
          }
        ]
      : []),
    {
      title: 'Extrato da Carteira',
      description: `${
        funcionario
          ? 'Aqui aparecem as movimentações do cartão escolhido.'
          : 'Tudo o que entra e sai da sua Carteira aparece aqui.'
      } Nas compras de passagem, clique em "Ver detalhes" para ver a viagem. Nas recargas, clique em "Ver recibo".`,
      target: extratoRef
    },
    {
      title: 'Seus dados',
      description:
        'Para ver ou atualizar seus dados, acesse seu perfil por aqui sempre que precisar.',
      target: { current: dadosContaEl }
    }
  ];

  const fecharDrawerAdicaoValor = () => {
    listaTransacoesRef.current?.atualizarTransacoes();
    CarteiraService.getCarteiras().then(setCarteiras);
    setDrawerAdicaoValorOpen(false);
  };

  useEffect(() => {
    if (!user) return;
    if (!('rg' in user) && !('cnpj' in user)) navigate(Routes.Cadastro.Pf, { state: user });
    else
      CarteiraService.getCarteiras()
        .then(setCarteiras)
        .finally(() => setCarteirasCarregadas(true));
  }, [navigate, user]);

  useEffect(() => {
    if (
      loading ||
      userClaim !== TipoUsuario.PESSOA_FISICA ||
      !user ||
      !('rg' in user) ||
      !location.state?.startWalletTour
    )
      return;

    setTourOpen(true);
    navigate(Routes.Home, { replace: true, state: null });
  }, [loading, location.state, navigate, user, userClaim]);

  // O perfil fica no cabeçalho a partir de md e no menu lateral abaixo disso.
  useEffect(() => {
    if (!tourOpen) return undefined;
    const atualizar = () =>
      setDadosContaEl(findVisibleTarget('[data-tour-pj="account"], [data-tour-pj="menu"]'));
    atualizar();
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, [tourOpen]);

  if (loading) return <Loading fullscreen show={loading} />;
  const carteiraBloqueada = carteiras.some((carteira) => carteira.saldoBloqueado);
  return (
    <>
      <Grid container spacing={2} paddingY={2}>
        <Grid item xs={12}>
          <Box
            component="header"
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={2}
            pb={1.75}
            borderBottom={`1px solid ${Colors.gray2}`}
          >
            <Typography variant="h5" color={Colors.secondary} fontWeight={700}>
              Extrato
            </Typography>
            <Box display="flex" alignItems="center" gap={1.25} flexWrap="wrap">
              <Button
                ref={recarregarSaldoRef}
                data-tour-pj="wallet-recharge"
                disabled={carteiraBloqueada}
                startIcon={
                  <Icon
                    path={mdiCurrencyUsd}
                    size={0.75}
                    color={carteiraBloqueada ? undefined : Colors.moneyGreen}
                  />
                }
                onClick={() => setDrawerAdicaoValorOpen(true)}
                sx={actionButtonSx}
              >
                Recarregar saldo
              </Button>
              <Divider orientation="vertical" flexItem sx={{ my: 0.5 }} />
              <Button
                ref={filtrarRef}
                data-tour-pj="wallet-filter"
                startIcon={<Icon path={mdiFilterVariant} size={0.75} />}
                onClick={() => listaTransacoesRef.current?.abrirFiltro()}
                sx={actionButtonSx}
              >
                Filtrar
              </Button>
            </Box>
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box ref={cartoesRef} width={{ lg: 'fit-content' }} maxWidth="100%">
            <ListarCartoes carteiras={carteiras} />
          </Box>
        </Grid>
        <Grid item xs={12} my={1}>
          <ListarTransacoes
            ref={listaTransacoesRef}
            usuarioAssociadoEmpresa={funcionario}
            tourRef={extratoRef}
            abasRef={abasRef}
            exibirAlvoTour={carteirasCarregadas}
          />
        </Grid>
      </Grid>

      {userClaim === TipoUsuario.PESSOA_FISICA && (
        <GuidedTour
          open={tourOpen}
          steps={tourSteps}
          label="Carteira"
          onClose={() => finalizarTour(SituacaoTour.Dispensado)}
          onComplete={() => finalizarTour(SituacaoTour.Concluido)}
        />
      )}

      {drawerAdicaoValorOpen && (
        <Box
          position="fixed"
          top={0}
          left={0}
          width={1}
          height={1}
          zIndex={1250}
          onClick={fecharDrawerAdicaoValor}
          sx={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        >
          <DrawerAdicionarSaldo open={drawerAdicaoValorOpen} onClose={fecharDrawerAdicaoValor} />
        </Box>
      )}
    </>
  );
};

export default Home;
